import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMarketplaceEnabled } from "../context/ConfigContext";
import { SignOut, Gavel, Shield, BookOpen, Gear } from "@phosphor-icons/react";
import ProseLogo from "./ProseLogo";
import ReadinessGauge from "./ReadinessGauge";
import { Button } from "./ui/button";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const marketplaceEnabled = useMarketplaceEnabled();

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="app-header">
      <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="app-logo-link">
          <ProseLogo size={40} />
          <span className="label-tiny ml-3 hidden md:inline">US Civil Complaint Drafting</span>
        </Link>
        <div className="flex items-center gap-3">
          {user && (
            <>
              <ReadinessGauge mode="compact" />
              <Link to="/resources" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-wider text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-300 hover:bg-slate-50" data-testid="resources-link">
                <BookOpen className="w-4 h-4" /> Resources
              </Link>
              <Link to="/account" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-wider text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-300 hover:bg-slate-50" data-testid="account-link">
                <Gear className="w-4 h-4" /> Account
              </Link>
              {user?.mfa_status?.enrolled && (
                <Link to="/settings/security" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-wider text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-300 hover:bg-slate-50" data-testid="security-link">
                  <Shield className="w-4 h-4" /> Security
                </Link>
              )}
              {marketplaceEnabled && (
                <Link to="/lawyer" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-wider text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-300 hover:bg-slate-50" data-testid="lawyer-portal-link">
                  <Gavel className="w-4 h-4" /> Lawyer Portal
                </Link>
              )}
              {user.is_admin && (
                <Link to="/admin" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-wider text-slate-900 bg-amber-100 border border-amber-300 px-3 py-1.5 hover:bg-amber-200" data-testid="admin-link">
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-sm font-medium text-slate-900" data-testid="user-name">{user.name}</span>
                <span className="text-xs text-slate-500" data-testid="user-email">{user.email}</span>
              </div>
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-slate-200" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">
                  {user.name?.[0]?.toUpperCase()}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                data-testid="logout-btn"
                className="rounded-none border-slate-300"
              >
                <SignOut className="w-4 h-4 mr-1.5" /> Sign out
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
